import { useWorkspace } from '../components/common/WorkspaceProvider';
import { useState, useEffect, useCallback } from 'react';
import { PromotionPresentationData } from '../types/promotion';
import { SystemSettings } from '../types/systemSettings';
import { getOtherProductsInfo } from '../services/promotionService';
import { getSystemSettings } from '../services/systemSettingsService';

/** 기존 공개 화면과 미리보기의 조회·화면 상태를 공유한다. */
export function usePromotionPresentation(promotion: PromotionPresentationData, hideElements?: string | null, propSystemSettings?: Partial<SystemSettings> | null) {
  const workspace = useWorkspace();
  const [selectedMenu, setSelectedMenu] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [otherProducts, setOtherProducts] = useState<{ [key: string]: { id: string; code: string; title: string } }>({});
  const [isLoadingOtherProducts, setIsLoadingOtherProducts] = useState(false);
  const [isSlidePanelOpen, setIsSlidePanelOpen] = useState(false);
  const [selectedProductCode, setSelectedProductCode] = useState<string | null>(null);
  const [localSystemSettings, setLocalSystemSettings] = useState<SystemSettings | null>(null);
  
  // props로 전달된 시스템 설정이 있으면 사용, 없으면 로컬에서 로드
  const systemSettings = propSystemSettings !== undefined ? propSystemSettings : localSystemSettings;

  // hideElements 파라미터 파싱
  const hiddenElements = hideElements ? hideElements.split(',').map(item => item.trim()) : [];

  // 시스템 설정 로드 (props로 전달되지 않은 경우에만)
  const loadSystemSettings = useCallback(async () => {
    if (propSystemSettings !== undefined) return; // null도 조회가 끝난 결과다.
    
    try {
      const settings = await getSystemSettings(workspace);
      setLocalSystemSettings(settings);
    } catch (error) {
      console.error('시스템 설정을 불러올 수 없습니다:', error);
    }
  }, [propSystemSettings, workspace]);

  // 다른제품 정보 로드
  useEffect(() => {
    const loadOtherProducts = async () => {
      const otherProductIds = [
        promotion.otherProduct1,
        promotion.otherProduct2,
        promotion.otherProduct3,
        promotion.otherProduct4
      ];

      // 다른제품이 하나라도 있는 경우에만 로드
      if (promotion.isActive && otherProductIds.some(id => id)) {
        setIsLoadingOtherProducts(true);
        try {
          const result = await getOtherProductsInfo(workspace, otherProductIds);
          if (result.success && result.data) {
            setOtherProducts(result.data);
          }
        } catch (error) {
          // 다른제품 정보 로드 실패 시 무시하고 진행
        } finally {
          setIsLoadingOtherProducts(false);
        }
      }
    };

    loadOtherProducts();
    loadSystemSettings();
  }, [promotion, loadSystemSettings, workspace]);





  const formatMonth = (month: string) => {
    if (!month) return '-';
    try {
      const [year, monthNum] = month.split('-');
      return `${year}년 ${parseInt(monthNum)}월`;
    } catch (error) {
      return month;
    }
  };

  // 다른제품 링크 클릭 핸들러
  const handleOtherProductClick = (productCode: string) => {
    setSelectedProductCode(productCode);
    setIsSlidePanelOpen(true);
  };

  // 슬라이드 패널 닫기 핸들러
  const handleCloseSlidePanel = () => {
    setIsSlidePanelOpen(false);
    setSelectedProductCode(null);
  };

  // 다른제품이 있는지 확인
  const hasOtherProducts = Object.keys(otherProducts).length > 0;

  return { selectedMenu, setSelectedMenu, isDialogOpen, setIsDialogOpen, otherProducts, isLoadingOtherProducts, isSlidePanelOpen, selectedProductCode, systemSettings, hiddenElements, formatMonth, handleOtherProductClick, handleCloseSlidePanel, hasOtherProducts };
}
